import { RequestService, type RequestOptions } from '../request-service';
import { AppsData } from './appsData';
import { TagSections } from '../tag-sections/tag-sections';
import type {
  GetAppsByAppIdsRequest,
  GetAppsByAppIdsResponse,
  GetDynamicSectionsRequest,
  GetDynamicSectionsResponse,
  GetTagSectionsRequest,
  GetTagSectionsResponse,
  DynamicSection,
  TagSection,
  GetPackageDataResponse,
} from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/types';
import type RpcModule from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/rpc';
import type HttpModule from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/http';
import type { AmbassadorBootstrap } from '@wix/ambassador-bootstrap-plugin';
import type { IHttpClient } from '@wix/http-client';
import type { QueryClient } from '@tanstack/react-query';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import { SearchAppsService } from '../app-market-search/search-apps.service';
import type {
  EnhancedSearchAppsResponse,
  EnhancedSearchAppsRequest,
  GetDynamicSectionsPayload,
  GetPackageDataPayload,
  PackageDataType,
} from './types';
import { PackageData } from './packageData';
import { EnhancedSearchHandler } from './enhanced-search';

type RpcModuleType = typeof RpcModule;
type HttpModuleType = typeof HttpModule;

export class AppMarketClientAggregatorService extends RequestService<
  RpcModuleType,
  HttpModuleType
> {
  private readonly searchAppsService: SearchAppsService;
  private readonly enhancedSearchHandler: EnhancedSearchHandler;
  private readonly installedAppsFirstTagIds = {
    hippa: '31bea3ce-ab6b-47c9-b89e-6b17493c644a',
    seo: '56a41fe5-29aa-4f51-a8c1-839fb7221985',
  };

  constructor(
    rpcClient?: AmbassadorBootstrap,
    httpClient?: IHttpClient,
    queryClient?: QueryClient,
    errorHandler?: ErrorHandlerPublicAPI,
  ) {
    super(rpcClient, httpClient, queryClient, errorHandler);
    this.searchAppsService = new SearchAppsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.enhancedSearchHandler = new EnhancedSearchHandler(
      this.request.bind(this),
      this.searchAppsService,
      this.installedAppsFirstTagIds,
    );
  }

  protected async rpcModule(): Promise<RpcModuleType> {
    return import(
      '@wix/ambassador-appmarket-v1-app-market-client-aggregator/rpc'
    );
  }

  protected async httpModule(): Promise<HttpModuleType> {
    return import(
      '@wix/ambassador-appmarket-v1-app-market-client-aggregator/http'
    );
  }

  private async enrichSectionsWithApps(
    sections: DynamicSection[] | undefined,
    options?: RequestOptions,
  ): Promise<DynamicSection[]> {
    if (!sections?.length) {
      return [];
    }

    const allAppIds = sections
      .filter((s) => s.appGroup?.appIds?.length && !s.appGroup?.apps?.length)
      .flatMap((s) => s.appGroup!.appIds!);

    if (!allAppIds.length) {
      return sections;
    }

    const appsData = await this.getAppsByAppIds({ appIds: allAppIds }, options);
    const appsMap = new Map(appsData.toJSON().map((app) => [app.id, app]));

    return sections.map((section) => {
      if (section.appGroup?.appIds?.length && !section.appGroup?.apps?.length) {
        const apps = section.appGroup.appIds
          .map((id) => appsMap.get(id))
          .filter((app): app is NonNullable<typeof app> => app !== undefined);
        return {
          ...section,
          appGroup: {
            ...section.appGroup,
            apps,
          },
        } as DynamicSection;
      }
      return section;
    });
  }

  private async enrichTagSectionsWithApps(
    sections: TagSection[] | undefined,
    options?: RequestOptions,
  ): Promise<TagSection[]> {
    if (!sections?.length) {
      return [];
    }

    const allAppIds = sections
      .filter((s) => s.appGroup?.appIds?.length && !s.appGroup?.apps?.length)
      .flatMap((s) => s.appGroup!.appIds!);

    if (!allAppIds.length) {
      return sections;
    }

    const appsData = await this.getAppsByAppIds({ appIds: allAppIds }, options);
    const appsMap = new Map(appsData.toJSON().map((app) => [app.id, app]));

    return sections.map((section) => {
      if (section.appGroup?.appIds?.length && !section.appGroup?.apps?.length) {
        const apps = section.appGroup.appIds
          .map((id) => appsMap.get(id))
          .filter((app): app is NonNullable<typeof app> => app !== undefined);
        return {
          ...section,
          appGroup: {
            ...section.appGroup,
            apps,
          },
        } as TagSection;
      }
      return section;
    });
  }

  // Server-side Express `qs` parser converts repeated query params over its
  // default `arrayLimit: 20` into an object with numeric keys, which then fails
  // proto binding with `appIds[0] must be a string`. Chunking client-side keeps
  // each request well under that limit.
  private static readonly APP_IDS_CHUNK_SIZE = 20;

  public async getAppsByAppIds(
    payload: GetAppsByAppIdsRequest,
    options?: RequestOptions,
  ): Promise<AppsData> {
    const appIds = payload.appIds ?? [];

    if (appIds.length <= AppMarketClientAggregatorService.APP_IDS_CHUNK_SIZE) {
      const response = await this.request<
        GetAppsByAppIdsRequest,
        GetAppsByAppIdsResponse
      >({
        methodName: 'getAppsByAppIds',
        payload,
        options,
      });
      return new AppsData(response?.apps || []);
    }

    const chunks: string[][] = [];
    for (
      let i = 0;
      i < appIds.length;
      i += AppMarketClientAggregatorService.APP_IDS_CHUNK_SIZE
    ) {
      chunks.push(
        appIds.slice(
          i,
          i + AppMarketClientAggregatorService.APP_IDS_CHUNK_SIZE,
        ),
      );
    }

    const responses = await Promise.all(
      chunks.map((chunkAppIds) =>
        this.request<GetAppsByAppIdsRequest, GetAppsByAppIdsResponse>({
          methodName: 'getAppsByAppIds',
          payload: { ...payload, appIds: chunkAppIds },
          options,
        }),
      ),
    );

    return new AppsData(responses.flatMap((response) => response?.apps ?? []));
  }

  public async getDynamicSections(
    payload: GetDynamicSectionsPayload,
    options?: RequestOptions,
  ): Promise<GetDynamicSectionsResponse> {
    const { placement, isMetaSiteId, relatedTo, keywords } = payload;
    const req: GetDynamicSectionsRequest = {
      placement,
      customParams: {
        isMetaSiteId: JSON.stringify(isMetaSiteId),
        ...(relatedTo && { relatedTo }),
        ...(keywords && { keywords }),
      },
    };

    const response = await this.request<
      GetDynamicSectionsRequest,
      GetDynamicSectionsResponse
    >({
      methodName: 'getDynamicSections',
      payload: req,
      options,
    });
    const enrichedSections = await this.enrichSectionsWithApps(
      response.sections,
      options,
    );
    return { ...response, sections: enrichedSections };
  }

  public async getPackageData(
    payload: GetPackageDataPayload,
    options?: RequestOptions,
  ): Promise<Omit<PackageDataType, 'icon' | 'teaser'> | undefined> {
    const response = await this.request<
      { packageId: string },
      GetPackageDataResponse
    >({
      methodName: 'getPackageData',
      payload: { packageId: payload.packageId },
      options,
    });
    return response.package
      ? new PackageData(response.package).toJSON()
      : undefined;
  }

  public async getTagSections(
    payload: GetTagSectionsRequest,
    options?: RequestOptions,
  ): Promise<TagSections> {
    const response = await this.request<
      GetTagSectionsRequest,
      GetTagSectionsResponse
    >({
      methodName: 'getTagSections',
      payload,
      options,
    });
    const enrichedSections = await this.enrichTagSectionsWithApps(
      response?.sections,
      options,
    );
    return new TagSections({ ...response, sections: enrichedSections });
  }

  /**
   * Searches apps with enhanced installed filter handling.
   *
   * @param request - Search request with enhanced filters (installed, tagId, searchTerm, etc.)
   * @returns Promise<EnhancedSearchAppsResponse> - Search results with appIds, hasMoreApps, totalApps
   *
   * Behavior:
   * - Examines request.filter.installed to determine search strategy
   * - Auto-detects: For hippa/seo tagIds, sets installed='installed-first' if not explicitly provided
   * - Routes to appropriate handler:
   *   • 'installed' → only installed apps
   *   • 'not-installed' → only not-installed apps
   *   • 'installed-first' → returns all installed apps (max 100, ignores request pagination) + not-installed apps (uses request pagination)
   *   • undefined → default search service
   *
   */

  public async enhancedSearchApps(
    request: EnhancedSearchAppsRequest,
  ): Promise<EnhancedSearchAppsResponse> {
    return this.enhancedSearchHandler.handleEnhancedSearchApps(request);
  }
}
